<template>
  <div>
    <div class="m-portlet m-portlet--mobile">
      <div class="m-portlet__head">
        <div class="m-portlet__head-caption">
          <div class="m-portlet__head-title">
            <h3 class="m-portlet__head-text" v-lang.reports></h3>
          </div>
        </div>
      </div>
      <div class="m-portlet__body m-portlet__body--no-padding">
        <div class="row no-gutters">
          <div class="col-lg-4 col-xl-3 report-list">
            <div v-for="group in reportGroups" :key="group.key" class="report-list__group">
              <div class="report-list__group-title">{{ translate(group.title) }}</div>
              <button
                v-for="report in group.reports"
                :key="report.key"
                type="button"
                class="report-list__item"
                :class="{ 'report-list__item--active': report.key === selectedKey }"
                @click="selectedKey = report.key">
                <i :class="report.icon"></i>
                <span>{{ translate(report.title) }}</span>
              </button>
            </div>
          </div>

          <div class="col-lg-8 col-xl-9 report-detail">
            <div class="report-detail__header">
              <h4 class="report-detail__title">{{ translate(selectedReport.title) }}</h4>
              <p class="report-detail__description">{{ translate(selectedReport.description) }}</p>
            </div>

            <!-- v-if degil v-show: jQuery tarih secicileri her rapor degisiminde yeniden kurulmasin. -->
            <div v-show="selectedReport.usesPeriod" class="report-period">
              <div class="report-period__head">
                <span class="report-period__title" v-lang.report_period></span>
                <button
                  v-for="preset in periodPresets"
                  :key="preset.key"
                  type="button"
                  class="report-period__preset"
                  :class="{ 'report-period__preset--active': isPresetActive(preset) }"
                  @click="applyPreset(preset)">
                  {{ translate(preset.key) }}
                </button>
              </div>
              <div class="report-period__row">
                <div class="report-period__date">
                  <date-picker v-model="startDate" :config="pickerDate" name="reportStartDate" :placeholder="translate('start_date')"/>
                </div>
                <span class="report-period__separator">–</span>
                <div class="report-period__date">
                  <date-picker v-model="endDate" :config="pickerDate" name="reportEndDate" :placeholder="translate('end_date')"/>
                </div>
                <button type="button" class="btn btn-primary report-period__download" @click="generate(selectedReport)">
                  <i class="fa fa-file-excel"></i>{{ translate('download_excel') }}
                </button>
              </div>
            </div>

            <div v-show="selectedReport.requiresPoints" class="report-detail__points">
              <div class="report-detail__section-title" v-lang.points></div>
              <!-- keep-alive: nokta agaci ilk acilista yuklenir, rapor degistirilip geri donulunce
                   yeniden yuklenmez. -->
              <keep-alive>
                <point-selector v-if="selectedReport.requiresPoints" v-model="selectedPoints"/>
              </keep-alive>
            </div>

            <template v-if="!selectedReport.usesPeriod">
              <div class="alert m-alert m-alert--default report-detail__note" role="alert" v-lang.report_period_independent_hint></div>
              <button type="button" class="btn btn-primary report-period__download" @click="generate(selectedReport)">
                <i class="fa fa-file-excel"></i>{{ translate('download_excel') }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ExcelJS from 'exceljs';
import { mixinz } from '@/scripts/global/mixinz';
import { groupBy } from '@/scripts/global/util';
import * as blockui from '@/helpers/block-ui';
import * as dateFormat from '@/helpers/dateFormat';
import { toastApiError } from '@/helpers/apiToast';
import PointSelector from './PointSelector.vue';

const DATE_FORMAT = 'DD.MM.YYYY';
const API_DATE_FORMAT = 'YYYY-MM-DD';

/** Donem kisayollari; sira ekrandaki dugme sirasidir. Anahtarlar ceviri anahtariyla ayni. */
const PERIOD_PRESETS = [
  { key: 'this_month', unit: 'month', offset: 0 },
  { key: 'last_month', unit: 'month', offset: -1 },
  { key: 'this_year', unit: 'year', offset: 0 },
];

/** Sol listedeki gruplar; sira ekrandaki siradir. */
const REPORT_GROUPS = [
  { key: 'workOrders', title: 'report_group_work_orders' },
  { key: 'rounds', title: 'report_group_rounds' },
  { key: 'staff', title: 'report_group_staff' },
  { key: 'assets', title: 'report_group_assets' },
];

/**
 * Dashboard & Raporlar > Raporlar. Raporlar eskiden ust bardaki Raporlar ve Hizli Eylemler acilir menulerinden
 * uretiliyordu; yetkiyle gizlenebilsin diye bu sayfaya tasindi (yetki: router.ts > Reports).
 * Solda gruplu liste, sagda secilen raporun ayrintisi. Donem raporlar arasinda ortaktir: rapor
 * degistirmek secili tarihleri sifirlamaz. Excel dosyalari tarayicida uretilir.
 */
export default {
  mixins: [mixinz],
  components: {
    'point-selector': PointSelector,
  },
  data() {
    return {
      selectedKey: 'workOrderIncident',
      selectedPoints: [],
      startDate: moment().startOf('month').format(DATE_FORMAT),
      endDate: moment().endOf('month').format(DATE_FORMAT),
      periodPresets: PERIOD_PRESETS,
      reports: [
        {
          key: 'workOrderIncident',
          group: 'workOrders',
          title: 'work_order_incident_report',
          description: 'report_desc_work_order_incident',
          icon: 'fa fa-clipboard-list',
          generator: 'generateWorkOrderIncidentReport',
          usesPeriod: true,
        },
        {
          key: 'periodicMaintenance',
          group: 'workOrders',
          title: 'periodic_maintenance_report',
          description: 'report_desc_periodic_maintenance',
          icon: 'fa fa-calendar-check',
          generator: 'generatePeriodicMaintenanceReport',
          usesPeriod: true,
        },
        {
          key: 'mtbfMttr',
          group: 'workOrders',
          title: 'mtbf_mttr_report',
          description: 'report_desc_mtbf_mttr',
          icon: 'fa fa-chart-line',
          generator: 'generateMtbfMttrReport',
          usesPeriod: true,
        },
        {
          key: 'gap',
          group: 'rounds',
          title: 'gap_report',
          description: 'report_desc_gap',
          icon: 'fa fa-exclamation-triangle',
          generator: 'generateGapReport',
          usesPeriod: true,
        },
        {
          key: 'pointAnalysis',
          group: 'rounds',
          title: 'pointAnalysisReport',
          description: 'report_desc_point_analysis',
          icon: 'fa fa-crosshairs',
          generator: 'generatePointAnalysisReport',
          usesPeriod: true,
          requiresPoints: true,
        },
        {
          key: 'staffPerformance',
          group: 'staff',
          title: 'staff_performance_report',
          description: 'report_desc_staff_performance',
          icon: 'fa fa-user-clock',
          generator: 'generateStaffPerformanceReport',
          usesPeriod: true,
        },
        {
          key: 'vendorPerformance',
          group: 'staff',
          title: 'vendor_performance_report',
          description: 'report_desc_vendor_performance',
          icon: 'fa fa-handshake',
          generator: 'generateVendorPerformanceReport',
          usesPeriod: true,
        },
        {
          key: 'allAsset',
          group: 'assets',
          title: 'all_asset',
          description: 'report_desc_all_asset',
          icon: 'fa fa-boxes',
          generator: 'generateAllAssetReport',
          usesPeriod: false,
        },
        {
          key: 'sparePartUsing',
          group: 'assets',
          title: 'spare_part_using_report',
          description: 'report_desc_spare_part_using',
          icon: 'fa fa-cogs',
          generator: 'generateSparePartUsingReport',
          usesPeriod: true,
        },
      ],
    };
  },
  computed: {
    reportGroups() {
      return REPORT_GROUPS.map((group) => ({
        ...group,
        reports: this.reports.filter((report) => report.group === group.key),
      }));
    },

    selectedReport() {
      return this.reports.find((report) => report.key === this.selectedKey);
    },
  },
  methods: {
    presetRange(preset) {
      const date = moment().add(preset.offset, preset.unit);
      return {
        startDate: date.clone().startOf(preset.unit).format(DATE_FORMAT),
        endDate: date.clone().endOf(preset.unit).format(DATE_FORMAT),
      };
    },

    applyPreset(preset) {
      Object.assign(this, this.presetRange(preset));
    },

    isPresetActive(preset) {
      const range = this.presetRange(preset);
      return range.startDate === this.startDate && range.endDate === this.endDate;
    },

    async generate(report) {
      if (report.usesPeriod && !this.isDateRangeValid(this.startDate, this.endDate)) return;
      // Bos secimde sakli yordam hic satir dondurmez; yalnizca basliklardan olusan dosya indirtme.
      if (report.requiresPoints && this.selectedPoints.length === 0) {
        toastr.error(this.translate('select_at_least_one_point'));
        return;
      }
      blockui.pageBlock(this);
      try {
        await this[report.generator](this.startDate, this.endDate);
      } catch (e) {
        toastApiError(e);
      } finally {
        blockui.unBlockPage();
      }
    },

    isDateRangeValid(startDate, endDate) {
      if (!startDate || !endDate) {
        toastr.error(this.translate('start_and_end_dates_are_required'));
        return false;
      }
      if (moment(startDate, DATE_FORMAT).toDate() > moment(endDate, DATE_FORMAT).toDate()) {
        toastr.error(this.translate('end_date_cannot_be_before_start_date'));
        return false;
      }
      return true;
    },

    toApiDate(date) {
      return moment(date, DATE_FORMAT).format(API_DATE_FORMAT);
    },

    async generateReports(reportName, context, startDate, endDate, workbook) {
      const dateMoment = new Date();
      let excelWorkbook = workbook;
      if (workbook === undefined) {
        excelWorkbook = new ExcelJS.Workbook();
        excelWorkbook.creator = 'CFM';
        excelWorkbook.created = dateMoment;
        excelWorkbook.calcProperties.fullCalcOnLoad = true;
        excelWorkbook.views = [
          {
            x: 0,
            y: 0,
            width: 10000,
            height: 20000,
            firstSheet: 0,
            activeTab: 1,
            visibility: 'visible',
          },
        ];
        if (context != null) {
          context.forEach((contextItem) => {
            // Add Work Sheet
            const sheet = excelWorkbook.addWorksheet(contextItem.sheetName, {});
            sheet.columns = contextItem.columnName;

            // Add Data
            contextItem.sheetData.forEach((row) => {
              sheet.addRow(row);
            });

            // Add Header Style
            sheet.eachRow((sheetRow, sheetRowNumber) => {
              if (sheetRowNumber == 1) {
                sheet.autoFilter = `${sheet.getColumn(1).letter + "1:" + sheet.getColumn(sheet.columns.length).letter + "1"}`;
              }

              sheetRow.eachCell((sheetCell) => {
                if (sheetRowNumber == 1) {
                  sheetCell.font = {
                    bold: true,
                  };

                  sheetCell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '0042a5f5' }
                  };
                }
              });

              sheetRow.commit();
            });
          });
        }
      }

      const buffer = await excelWorkbook.xlsx.writeBuffer();
      const createdAt = moment(dateMoment, DATE_FORMAT).format('YYYYMMDDHHmm');
      if (startDate != null && endDate != null) {
        const range = `${moment(startDate, DATE_FORMAT).format('YYYYMMDD')}-${moment(endDate, DATE_FORMAT).format('YYYYMMDD')}`;
        this.saveByteArray(`${reportName}_${createdAt}_${range}.xlsx`, buffer);
      } else {
        this.saveByteArray(`${reportName}_${createdAt}.xlsx`, buffer);
      }
    },

    saveByteArray(reportName, byte) {
      const blob = new Blob([byte], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      if (navigator.appVersion.toString().indexOf('.NET') > 0) window.navigator.msSaveBlob(blob, reportName);
      else {
        const link = document.createElementNS('http://www.w3.org/1999/xhtml', 'a');
        link.href = URL.createObjectURL(blob);
        link.download = reportName;
        link.click();
      }
    },

    async generateWorkOrderIncidentReport(startDate, endDate) {
      const res = await this.$globalApiClient.report.getWorkOrderIncidentReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });

      const reportColumns = [
        { key: "RoomName", header: this.translate('room'), width: 20 },
        { key: "InventoryId", header: this.translate('inventory_id'), width: 20 },
        { key: "InventoryCode", header: this.translate('inventory_code'), width: 20 },
        { key: "InventoryName", header: this.translate('inventory_name'), width: 20 },
        { key: "InfrastructureName", header: this.translate('infrastructure'), width: 20 },
        { key: "CapacityTypeName", header: this.translate('capacity_type'), width: 20 },
        { key: "DeviceSerialNumber", header: this.translate('serial_number'), width: 20 },
        { key: "DeviceBrandName", header: this.translate('brand'), width: 20 },
        { key: "DeviceModelName", header: this.translate('model'), width: 20 },
        { key: "OrderSourceName", header: this.translate('order_type'), width: 20 },
        { key: "OrderId", header: this.translate('order_id'), width: 20 },
        { key: "OrderStatusName", header: this.translate('order_status_report'), width: 20 },
        { key: "WorkCategoryName", header: this.translate('task_category'), width: 20 },
        { key: "WorkGroupName", header: this.translate('work_group'), width: 20 },
        { key: "PeriodName", header: this.translate('period'), width: 20 },
        { key: "OrderName", header: this.translate('order_name'), width: 20 },
        { key: "ContractName", header: this.translate('contract_name'), width: 20 },
        { key: "OrderPriority", header: this.translate('priority'), width: 20 },
        { key: "AssignedBy", header: this.translate('assigned_by'), width: 20 },
        { key: "ScheduledTime", header: this.translate('scheduled_date'), width: 20, style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "ActualDate", header: this.translate('actual_date'), width: 20, style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "Duration", header: this.translate('scheduled_duration_report'), width: 20 },
        { key: "ActualDuration", header: this.translate('actual_duration_report'), width: 20 },
        { key: "ApprovalBy", header: this.translate('sent_by_for_approval_report'), width: 20 },
        { key: "ApprovedBy", header: this.translate('confirmed_by_report'), width: 20 },
        { key: "HasFeedback", header: this.translate('has_feedback'), width: 20 }
      ];

      const reportRows = res.data.map((row) => {
        return {
          RoomName: row.RoomName,
          InventoryId: row.InventoryId,
          InventoryCode: row.InventoryCode,
          InventoryName: row.InventoryName,
          InfrastructureName: row.InfrastructureName,
          CapacityTypeName: row.CapacityTypeName,
          DeviceSerialNumber: row.DeviceSerialNumber,
          DeviceBrandName: row.DeviceBrandName,
          DeviceModelName: row.DeviceModelName,
          OrderSourceName: row.OrderSourceName,
          OrderId: row.OrderId,
          OrderStatusName: row.OrderStatusName,
          WorkCategoryName: row.WorkCategoryName,
          WorkGroupName: row.WorkGroupName,
          PeriodName: row.PeriodName,
          OrderName: row.OrderName,
          ContractName: row.ContractName,
          OrderPriority: row.OrderPriority,
          AssignedBy: row.AssignedBy,
          ScheduledTime: row.ScheduledTime ? dateFormat.viewDateTimeFormatTZ(row.ScheduledTime) : null,
          ActualDate: row.ActualDate ? dateFormat.viewDateTimeFormatTZ(row.ActualDate) : null,
          Duration: row.Duration,
          ActualDuration: row.ActualDuration,
          ApprovalBy: row.ApprovalBy,
          ApprovedBy: row.ApprovedBy,
          HasFeedback: row.HasFeedback ? this.translate('yes') : this.translate('no')
        };
      });

      const context = [
        {
          sheetName: this.translate('work_order_incident_sheet'),
          sheetData: reportRows,
          columnName: reportColumns,
        }
      ];

      await this.generateReports(this.translate('work_order_incident_report_excel_name'), context, startDate, endDate);
    },

    async generateGapReport(startDate, endDate) {
      const res = await this.$globalApiClient.report.reportGenerateGapReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });
      const roundReportData = res.data;

      // Round Bilgileri Start
      const roundColumns = [
        { key: "Name", header: this.translate('round_name'), width: 15 },
        { key: "RoundCount", header: this.translate('how_many_times'), width: 15 },
        { key: "AddedFlagCount", header: this.translate('how_many_flags_have_been_added'), width: 15 },
        { key: "FoundInvalid", header: this.translate('number_of_invalid_answers_deviating_from_normal'), width: 15 },
      ];

      const roundList = roundReportData.Rounds.map((round) => {
        return {
          Name: round.Name,
          RoundCount: round.RoundCount,
          AddedFlagCount: round.AddedFlagCount,
          FoundInvalid: round.FoundInvalid,
        };
      });
      // Round Bilgileri End

      // Envanter Bilgileri Start
      const inventoryColumns = [
        { key: "DeviceName", header: this.translate('capacity_equipment'), width: 20 },
        { key: "ParentDeviceName", header: this.translate('device'), width: 20 },
        { key: "PointName", header: this.translate('point_name'), width: 15 },
        { key: "RoomName", header: this.translate('room'), width: 15 },
        { key: "ControlCount", header: this.translate('how_many_times_check'), width: 15 },
        { key: "InvalidPointCount", header: this.translate('how_many_times_abnormal_answer_received'), width: 15 },
      ];

      const inventoryList = roundReportData.Inventories.map((x) => {
        return {
          DeviceName: x.DeviceName,
          ParentDeviceName: x.ParentDeviceName,
          PointName: x.PointName,
          RoomName: x.RoomName,
          ControlCount: x.ControlCount,
          InvalidPointCount: x.InvalidPointCount,
        };
      });
      // Envanter Bilgileri End

      // Personnel Bilgileri Start
      const personnelColumns = [
        { key: "Name", header: this.translate('staff'), width: 15 },
        { key: "FoundInvalid", header: this.translate('number_of_deviations_from_normal_found'), width: 15 },
        { key: "AddedFlagCount", header: this.translate('number_of_warning_flags_added'), width: 15 },
      ];

      const personnelList = roundReportData.Personnel.map((x) => {
        return {
          Name: x.Name,
          FoundInvalid: x.FoundInvalid,
          AddedFlagCount: x.AddedFlagCount,
        };
      });
      // Personnel Bilgileri End

      const context = [
        {
          sheetName: this.translate('round_information'),
          sheetData: roundList,
          columnName: roundColumns,
        },
        {
          sheetName: this.translate('inventory_information'),
          sheetData: inventoryList,
          columnName: inventoryColumns,
        },
        {
          sheetName: this.translate('staff_information'),
          sheetData: personnelList,
          columnName: personnelColumns,
        }
      ];
      await this.generateReports("GapReport", context, startDate, endDate);
    },

    async generatePeriodicMaintenanceReport(startDate, endDate) {
      const self = this;
      const res = await this.$globalApiClient.workOrder.workOrderGetPeriodicMaintenanceReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });

      const columns = [
        { key: 'Id', header: 'Id' },

        { key: 'InfrastructureName', header: self.translate('infrastructure') },
        { key: 'CapacityTypeName', header: self.translate('capacity_type') },
        { key: 'WorkGroupName', header: self.translate('work_group') },
        { key: 'Name', header: self.translate('name') },
        { key: 'DeviceName', header: self.translate('device') },
        { key: 'PeriodName', header: self.translate('maintenance_period') },
        { key: 'PlannedMaintenanceDate', header: self.translate('planned_maintenance_date') },
        { key: 'PerformedMaintenanceDate', header: self.translate('performed_maintenance_date') },
        { key: 'StatusName', header: self.translate('status') },
        { key: 'DefermentCondition', header: self.translate('deferment_condition') },
        { key: 'IsDelayed', header: self.translate('is_delayed') },
        { key: 'TimeElapsedSinceLastMaintenance', header: self.translate('time_elapsed_since_last_maintenance') },
      ];

      const rows = res.data.map((x) => {
        let isDelayed = null;
        if (x.PerformedMaintenanceDate) {
          isDelayed = moment(x.PerformedMaintenanceDate).startOf('day').diff(moment(x.PlannedMaintenanceDate).startOf('day'), 'days') > x.DefermentCondition;
        }

        return {
          ...x,
          IsDelayed: isDelayed,
          TimeElapsedSinceLastMaintenance: x.PerformedMaintenanceDate === null ? null : moment().startOf('day').diff(moment(x.PerformedMaintenanceDate).startOf('day'), 'days'),
        };
      });

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'CFM';
      workbook.created = new Date();
      workbook.calcProperties.fullCalcOnLoad = true;
      workbook.views = [
        {
          x: 0,
          y: 0,
          width: 10000,
          height: 20000,
          firstSheet: 0,
          activeTab: 1,
          visibility: 'visible',
        },
      ];

      const sheet = workbook.addWorksheet('Data', {});
      sheet.columns = columns;

      const rowsGroupedByWorkId = groupBy(rows, (x) => x.WorkId);
      const keys = Array.from(rowsGroupedByWorkId.keys());
      for (let j = 0; j < keys.length; j++) {
        const workId = keys[j];

        const rowsByWorkId = rowsGroupedByWorkId.get(workId);
        const rowByWorkIdCount = rowsByWorkId.length;

        const capacityTypeNameCol = columns.findIndex((x) => x.key == 'CapacityTypeName') + 1;
        const timeElapsedSinceLastMaintenanceCol = columns.findIndex((x) => x.key == 'TimeElapsedSinceLastMaintenance') + 1;
        const isDelayedCol = columns.findIndex((x) => x.key == 'IsDelayed') + 1;
        const performedMaintenanceDateCol = columns.findIndex((x) => x.key == 'PerformedMaintenanceDate') + 1;
        const plannedMaintenanceDateCol = columns.findIndex((x) => x.key == 'PlannedMaintenanceDate') + 1;
        for (let i = 0; i < rowByWorkIdCount; i++) {
          const createdRow = sheet.addRow(rowsByWorkId[i]);
          createdRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            if (!cell.style || !cell.style.border) {
              cell.style = { border: {} };
            }
            if (j == 0 && i == 0) {
              cell.style.border.top = { style: 'thin', color: { argb: '00000000' } };
            }
            if (colNumber == capacityTypeNameCol) {
              cell.style.border.right = { style: 'thin', color: { argb: '00000000' } };
            }
            if (colNumber == timeElapsedSinceLastMaintenanceCol) {
              cell.style.border.left = { style: 'thin', color: { argb: '00000000' } };
            }
            if (colNumber == isDelayedCol && cell.value !== null) {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: cell.value ? 'FFFF0000' : 'FF00FF00' },
              };
              cell.value = cell.value ? self.translate('yes') : self.translate('no');
            }
            if (cell.value !== null && colNumber == performedMaintenanceDateCol || colNumber == plannedMaintenanceDateCol) {
              cell.value = moment(cell.value).toDate();
            }

            if (i == rowByWorkIdCount - 1) {
              cell.style.border.bottom = { style: 'thin', color: { argb: '00000000' } };
            }
          });
        }
      }

      sheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength;
      });

      // Add Header Style
      sheet.eachRow((sheetRow, sheetRowNumber) => {
        if (sheetRowNumber == 1) {
          sheet.autoFilter = `${sheet.getColumn(1).letter + "1:" + sheet.getColumn(sheet.columns.length).letter + "1"}`;
        }

        sheetRow.eachCell((sheetCell) => {
          if (sheetRowNumber == 1) {
            sheetCell.font = {
              bold: true,
            };

            sheetCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '0042a5f5' }
            };
          }
        });

        sheetRow.commit();
      });

      await this.generateReports('PeriodicMaintenanceReport', undefined, startDate, endDate, workbook);
    },

    async generateAllAssetReport() {
      const self = this;
      const res = await this.$globalApiClient.report.reportGetAssetsReport();

      const columns = [
        { key:"Id", header:"No" },
        { key:"Name", header:self.translate("name") },
        { key:"ParentDevice", header:"Parent Device" },
        { key:"Domain", header:"Domain" },
        { key:"AssetType", header:"Asset Tipi" },
        { key:"ManufacturerName", header:"Marka" },
        { key:"ModelName", header:"Model" },
        { key:"SerialNumber", header:"Seri No" },
        { key:"Critical", header:"Critical" },
        { key:"AssetCapacity", header:"Kapasite" },
        { key:"ProductionYear", header:"Üretim Yılı" },
        { key:"DateOfInstallation", header:"Kurulum Tarihi", style: { numFmt: 'dd/mm/yyyy' } },
        { key:"Location", header:"Lokasyon" },
        { key:"GuaranteeStartYear", header:"Garanti Başlangıç Tarihi", style: { numFmt: 'dd/mm/yyyy' } },
        { key:"GuaranteeExpireYear", header:"Garanti Bitiş Tarihi", style: { numFmt: 'dd/mm/yyyy' } },
        { key:"MaintenancePeriod", header:"Önerilen Bakım Aralığı" },
        { key:"MOP", header:"MOP" },
        { key:"SOP", header:"SOP" },
        { key:"MaintenanceContractStartDate", header:"Bakım Anlaşma Başlangıç Tarihi", style: { numFmt: 'dd/mm/yyyy' } },
        { key:"MaintenanceContractExpireDate", header:"Bakım Anlaşma Bitiş Tarihi", style: { numFmt: 'dd/mm/yyyy' } },
        { key:"MaintenanceRenewalRemainingDay", header:"Bakım Anlaşma Yenileme Kalan Gün" },
        { key:"ApproximateLifeTime", header:"Yaklaşık Ömür" },
        { key:"ApproximateRemainingYear", header:"Yaklaşık Kalan Ömür (yıl)" },
      ];

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'CFM';
      workbook.created = new Date();
      workbook.calcProperties.fullCalcOnLoad = true;
      workbook.views = [
        {
          x: 0, y: 0, width: 10000, height: 20000,
          firstSheet: 0, activeTab: 1, visibility: 'visible'
        }
      ];

      const sheet = workbook.addWorksheet("Data", {});
      sheet.columns = columns;

      const reportRows = res.data.map((row) => {
        return {
          Id: row.Id,
          Name: row.Name,
          ParentDevice: row.ParentDevice,
          Domain: row.Domain,
          AssetType: row.AssetType,
          ManufacturerName: row.ManufacturerName,
          ModelName: row.ModelName,
          SerialNumber: row.SerialNumber,
          Critical: row.Critical,
          AssetCapacity: row.AssetCapacity,
          ProductionYear: row.ProductionYear,
          DateOfInstallation: row.DateOfInstallation ? dateFormat.viewDateFormatTZ(row.DateOfInstallation) : null,
          Location: row.Location,
          GuaranteeStartYear: row.GuaranteeStartYear ? dateFormat.viewDateFormatTZ(row.GuaranteeStartYear) : null,
          GuaranteeExpireYear: row.GuaranteeExpireYear ? dateFormat.viewDateFormatTZ(row.GuaranteeExpireYear) : null,
          MaintenancePeriod: row.MaintenancePeriod,
          MOP: row.MOP,
          SOP: row.SOP,
          MaintenanceContractStartDate: row.MaintenanceContractStartDate ? dateFormat.viewDateFormatTZ(row.MaintenanceContractStartDate) : null,
          MaintenanceContractExpireDate: row.MaintenanceContractExpireDate ? dateFormat.viewDateFormatTZ(row.MaintenanceContractExpireDate) : null,
          MaintenanceRenewalRemainingDay: row.MaintenanceRenewalRemainingDay,
          ApproximateLifeTime: row.ApproximateLifeTime,
          ApproximateRemainingYear: row.ApproximateRemainingYear
        };
      });

      sheet.addRows(reportRows);

      sheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = maxLength < 10 ? 10 : maxLength;
      });

      sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell((cell) => {
          if (rowNumber == 1) {
            cell.font = { name: 'Calibri', family: 1, bold: true, size: 11 };

            for (let i = 1; i < sheet.columns.length + 1; i++) {
              if (i < 5) {
                row.getCell(i).fill = { type: 'pattern', pattern:'solid', fgColor:{argb:'17cbcf'} };
              }
              else if (i < 14 && i > 4) {
                row.getCell(i).fill = { type: 'pattern', pattern:'solid', fgColor:{argb:'8a8ef2'} };
              }
              else if (i > 13 && i < 19) {
                row.getCell(i).fill = { type: 'pattern', pattern:'solid', fgColor:{argb:'a2a823'} };
              }
              else {
                row.getCell(i).fill = { type: 'pattern', pattern:'solid', fgColor:{argb:'23a894'} };
              }
            }
          }
          else {
            cell.font = { name: 'Calibri', family: 1, bold: false, size: 10 };
          }

          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
      });

      await this.generateReports("AssetsReport_", undefined, undefined, undefined, workbook);
    },

    async generateStaffPerformanceReport(startDate, endDate) {
      const res = await this.$globalApiClient.report.reportGetStaffPerformanceReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });
      const staffPerformanceData = res.data;
      const StaffPerformanceColumn = [
        { key: "StaffName", header: "Personel Adı", width: 25 },
        { key: "OrderCount", header: "İş Emri Sayısı", width: 15 },
        { key: "SpentTime", header: "Harcanan Süre(Dakika)", width: 10 },
        { key: "ShiftTime", header: "Vardiya Çalışma Süre(Dakika)", width: 10 },
        { key: "Overtime", header: "Mesai Çalışma Süre(Dakika)", width: 10 },
        { key: "InvalidAnswerCount", header: "Geçersiz Yanıt Sayısı", width: 20 },
        { key: "FlagCount", header: "Bayrak Sayısı", width: 15 },
      ];
      const StaffExcuseColumn = [
        { key: "StaffName", header: "Personel Adı", width: 25 },
        { key: "ShiftTime", header: "Vardiya Çalışma Süre(Dakika)", width: 10 },
        { key: "Overtime", header: "Mesai Çalışma Süre(Dakika)", width: 10 },
        { key: "AnnualExcuse", header: "Yıllık İzin(Dakika)", width: 25 },
        { key: "IllnessExcuse", header: "Hastalık İzni(Dakika)", width: 25 },
        { key: "OtherExcuse", header: "Diğer İzinler(Dakika)", width: 25 }
      ];

      const toPerformanceRow = (x) => ({
        StaffName: x.StaffName,
        OrderCount: x.OrderCount,
        SpentTime: x.SpentTime,
        ShiftTime: x.ShiftTime,
        Overtime: x.Overtime,
        InvalidAnswerCount: x.InvalidAnswerCount,
        FlagCount: x.FlagCount,
      });
      const rowsStaffExcuseData = staffPerformanceData.StaffExcuseList.map((x) => {
        return {
          StaffName: x.StaffName,
          ShiftTime: x.ShiftTime,
          Overtime: x.Overtime,
          AnnualExcuse: x.AnnualExcuse,
          IllnessExcuse: x.IllnessExcuse,
          OtherExcuse: x.OtherExcuse
        };
      });

      const context = [
        {
          sheetName: 'Önleyici Bakım Listesi',
          sheetData: staffPerformanceData.PreventativeMaintenenceList.map(toPerformanceRow),
          columnName: StaffPerformanceColumn,
        },
        {
          sheetName: 'Round Listesi',
          sheetData: staffPerformanceData.RoundList.map(toPerformanceRow),
          columnName: StaffPerformanceColumn,
        },
        {
          sheetName: 'Olay Listesi',
          sheetData: staffPerformanceData.IncidentList.map(toPerformanceRow),
          columnName: StaffPerformanceColumn,
        },
        {
          sheetName: 'İzin Listesi',
          sheetData: rowsStaffExcuseData,
          columnName: StaffExcuseColumn,
        },
      ];
      await this.generateReports("StaffPerformanceReport", context, startDate, endDate);
    },

    async generateVendorPerformanceReport(startDate, endDate) {
      const res = await this.$globalApiClient.report.reportGetVendorWorkTimeReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });

      const vendorPerformanceColumns = [
        { key: "OrderType", header: this.translate('type'), width: 10 },
        { key: "VendorName", header: this.translate('company'), width: 15 },
        { key: "UserName", header: this.translate('vendor'), width: 15 },
        { key: "VendorStartDate", header: this.translate('start_date'), width: 20 },
        { key: "VendorEndDate", header: this.translate('end_date'), width: 20 },
        { key: "VendorSpentTime", header: this.translate('time_spent_minutes'), width: 15 },
        { key: "InventoryName", header: this.translate('inventory'), width: 15 },
      ];

      const vendorPerformanceData = res.data.map((x) => {
        return {
          OrderType: x.OrderType,
          VendorName: x.VendorName,
          UserName: x.UserName,
          VendorStartDate: x.VendorStartDate ? moment(x.VendorStartDate).format('YYYY-MM-DD HH:mm') : '',
          VendorEndDate: x.VendorEndDate ? moment(x.VendorEndDate).format('YYYY-MM-DD HH:mm') : '',
          VendorSpentTime: x.VendorSpentTime,
          InventoryName: x.InventoryName,
        };
      });

      const context = [
        {
          sheetName: 'Veri',
          sheetData: vendorPerformanceData,
          columnName: vendorPerformanceColumns,
        }
      ];
      await this.generateReports("VendorPerformanceReport", context, startDate, endDate);
    },

    async generateSparePartUsingReport(startDate, endDate) {
      const res = await this.$globalApiClient.report.reportGetSparePartUsingReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });
      const sparePartUsingReport = res.data;

      const sparePartUsingReportDataColumns = [
        { key: "SparePartId", header: this.translate('spare_part_report_id') },
        { key: "SparePartName", header: this.translate('spare_part_report_name') },
        { key: "WarehouseName", header: this.translate('spare_part_report_warehouse_name') },
        { key: "SerialNumber", header: this.translate('spare_part_report_serial_number') },
        { key: "SparePartCategoryName", header: this.translate('spare_part_report_category_name') },
        { key: "SparePartSubCategoryName", header: this.translate('spare_part_report_sub_category_name') },
        { key: "DeviceName", header: this.translate('spare_part_report_device_name') },
        { key: "SparePartKindName", header: this.translate('spare_part_report_kind_name') },
        { key: "BrandName", header: this.translate('spare_part_report_brand_name') },
        { key: "ModelName", header: this.translate('spare_part_report_model_name') },
        { key: "WorkOrderId", header: this.translate('spare_part_report_work_order_id') },
        { key: "WorkOrderName", header: this.translate('spare_part_report_work_order_name') },
        { key: "CreatedDate", header: this.translate('spare_part_report_created_date'), style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "Amount", header: this.translate('spare_part_report_amount'), style: { numFmt: '#' } },
        { key: "UnitName", header: this.translate('spare_part_report_unit_name') }
      ];

      const sparePartUsingReportData = sparePartUsingReport.UsingData.map((row) => {
        return {
          SparePartId: row.SparePartId,
          SparePartName: row.SparePartName,
          WarehouseName: row.WarehouseName,
          SerialNumber: row.SerialNumber,
          SparePartCategoryName: row.SparePartCategoryName,
          SparePartSubCategoryName: row.SparePartSubCategoryName,
          DeviceName: row.DeviceName,
          SparePartKindName: row.SparePartKindName,
          BrandName: row.BrandName,
          ModelName: row.ModelName,
          WorkOrderId: row.WorkOrderId,
          VendorSpentTime: row.VendorSpentTime,
          WorkOrderName: row.WorkOrderName,
          CreatedDate: row.CreatedDate ? dateFormat.viewDateTimeFormatTZ(row.CreatedDate) : null,
          Amount: row.Amount ? parseInt(row.Amount) : null,
          UnitName: row.UnitName
        };
      });

      const sparePartUsingStatusDataColumns = [
        { key: "SparePartId", header: this.translate('spare_part_report_id') },
        { key: "UniqueCode", header: this.translate('unique_id') },
        { key: "SparePartName", header: this.translate('spare_part_report_name') },
        { key: "WarehouseName", header: this.translate('spare_part_report_warehouse_name') },
        { key: "SerialNumber", header: this.translate('spare_part_report_serial_number') },
        { key: "SparePartCategoryName", header: this.translate('spare_part_report_category_name') },
        { key: "SparePartSubCategoryName", header: this.translate('spare_part_report_sub_category_name') },
        { key: "DeviceName", header: this.translate('spare_part_report_device_name') },
        { key: "SparePartKindName", header: this.translate('spare_part_report_kind_name') },
        { key: "BrandName", header: this.translate('spare_part_report_brand_name') },
        { key: "ModelName", header: this.translate('spare_part_report_model_name') },
        { key: "WorkOrderId", header: this.translate('spare_part_report_work_order_id') },
        { key: "WorkOrderName", header: this.translate('spare_part_report_work_order_name') },
        { key: "CreatedDate", header: this.translate('spare_part_report_created_date'), style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "SparePartUsingStatusName", header: this.translate('status') },
        { key: "UsingTime", header: this.translate('spare_part_using_time') }
      ];

      const sparePartUsingStatusData = sparePartUsingReport.StatusData.map((row) => {
        return {
          SparePartId: row.SparePartId,
          UniqueCode: row.UniqueCode,
          SparePartName: row.SparePartName,
          WarehouseName: row.WarehouseName,
          SerialNumber: row.SerialNumber,
          SparePartCategoryName: row.SparePartCategoryName,
          SparePartSubCategoryName: row.SparePartSubCategoryName,
          DeviceName: row.DeviceName,
          SparePartKindName: row.SparePartKindName,
          BrandName: row.BrandName,
          ModelName: row.ModelName,
          WorkOrderId: row.WorkOrderId,
          WorkOrderName: row.WorkOrderName,
          CreatedDate: row.CreatedDate ? dateFormat.viewDateTimeFormatTZ(row.CreatedDate) : null,
          SparePartUsingStatusName: row.SparePartUsingStatusName,
          UsingTime: row.UsingTime
        };
      });

      const context = [
        {
          sheetName: this.translate('spare_part_using_report_fs'),
          sheetData: sparePartUsingReportData,
          columnName: sparePartUsingReportDataColumns
        },
        {
          sheetName: this.translate('spare_part_using_report_ss'),
          sheetData: sparePartUsingStatusData,
          columnName: sparePartUsingStatusDataColumns
        }
      ];

      await this.generateReports("SparePartUsingReport", context, startDate, endDate);
    },

    async generateMtbfMttrReport(startDate, endDate) {
      const res = await this.$globalApiClient.report.reportGetMtbfMttrReport({
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });
      const mtbfMttrReportData = res.data;

      // Summary Sheet Start
      const summaryColumns = [
        { key: "CapacityEquipmentName", header: this.translate('capacity_equipment'), width: 20 },
        { key: "DeviceName", header: this.translate('device_name'), width: 20 },
        { key: "FailureCount", header: this.translate('total_failure_count'), width: 15 },
        { key: "ElapsedTime", header: this.translate('total_failure_duration'), width: 20 },
        { key: "FormattedMtbf", header: this.translate('mtbf'), width: 20 },
        { key: "FormattedMttr", header: this.translate('mttr'), width: 20 },
        { key: "Performance", header: this.translate('mtbf_mttr_perfomance'), width: 25 }
      ];

      const summaryList = mtbfMttrReportData.SummaryData.map((x) => {
        return {
          CapacityEquipmentName: x.CapacityEquipmentName,
          DeviceName: x.DeviceName,
          FailureCount: x.FailureCount,
          ElapsedTime: x.ElapsedTime,
          FormattedMtbf: x.FormattedMtbf,
          FormattedMttr: x.FormattedMttr,
          Performance: x.Performance
        };
      });
      // Summary Sheet End

      // Detail Sheet Start
      const detailColumns = [
        { key: "CapacityEquipmentName", header: this.translate('capacity_equipment'), width: 20 },
        { key: "DeviceName", header: this.translate('device_name'), width: 20 },
        { key: "StartDate", header: this.translate('failure_start_time'), width: 20, style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "EndDate", header: this.translate('failure_end_time'), width: 20, style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "FailureTime", header: this.translate('failure_duration'), width: 15 },
        { key: "FailureTypeName", header: this.translate('failure_mode'), width: 20 },
        { key: "Description", header: this.translate('description'), width: 20 },
        { key: "WorkOrderId", header: this.translate('work_order_id'), width: 15 },
        { key: "WorkOrderName", header: this.translate('work_order_name'), width: 20 },
        { key: "CreatedUserName", header: this.translate('created_by'), width: 15 },
        { key: "ModifiedUserName", header: this.translate('last_modified_by'), width: 15 },
        { key: "LastModifiedDate", header: this.translate('last_modified_date'), width: 20, style: { numFmt: 'dd/mm/yyyy HH:mm' } },
      ];

      const detailList = mtbfMttrReportData.DetailData.map((x) => {
        return {
          CapacityEquipmentName: x.CapacityEquipmentName,
          DeviceName: x.DeviceName,
          StartDate: x.StartDate ? dateFormat.viewDateTimeFormatTZ(x.StartDate) : null,
          EndDate: x.EndDate ? dateFormat.viewDateTimeFormatTZ(x.EndDate) : null,
          FailureTime: x.FailureTime,
          FailureTypeName: x.FailureTypeName,
          Description: x.Description,
          WorkOrderId: x.WorkOrderId,
          WorkOrderName: x.WorkOrderName,
          CreatedUserName: x.CreatedUserName,
          ModifiedUserName: x.ModifiedUserName,
          LastModifiedDate: x.LastModifiedDate ? dateFormat.viewDateTimeFormatTZ(x.LastModifiedDate) : null,
        };
      });
      // Detail Sheet End

      const context = [
        {
          sheetName: this.translate('mtbf_mttr_summary'),
          sheetData: summaryList,
          columnName: summaryColumns,
        },
        {
          sheetName: this.translate('mtbf_mttr_detail'),
          sheetData: detailList,
          columnName: detailColumns,
        },
      ];

      await this.generateReports("MtbfMttrReport", context, startDate, endDate);
    },

    async generatePointAnalysisReport(startDate, endDate) {
      const res = await this.$globalApiClient.pointAnalysis.GetPointAnalysisReport({
        pointIds: this.selectedPoints.map((point) => point.Id).toString(),
        startDate: this.toApiDate(startDate),
        endDate: this.toApiDate(endDate),
      });

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'CFM';
      workbook.created = new Date();
      workbook.calcProperties.fullCalcOnLoad = true;
      workbook.views = [
        {
          x: 0,
          y: 0,
          width: 10000,
          height: 20000,
          firstSheet: 0,
          activeTab: 1,
          visibility: 'visible',
        },
      ];

      const sheet = workbook.addWorksheet("PointAnalysis", {});
      sheet.columns = [
        { key: "WorkGroupName", header: "İş Gurubu", width: 20 },
        { key: "WorkName", header: "İş Adı", width: 20 },
        { key: "Room", header: "Oda", width: 20 },
        { key: "Inventory", header: "Envanter", width: 20 },
        { key: "Device", header: "Cihaz", width: 20 },
        { key: "PointName", header: "Nokta İsmi", width: 20 },
        { key: "ScheduledTime", header: "Tarih Saat", width: 20, style: { numFmt: 'dd/mm/yyyy HH:mm' } },
        { key: "WorkOrderId", header: "WO NO", width: 20 },
        { key: "AnswerValue", header: "Değer", width: 20 },
        { key: "ExpectableValue", header: "Beklenen Değer (Y/N)", width: 20 },
      ];

      res.data.forEach((row) => {
        sheet.addRow({
          WorkGroupName: row.WorkGroupName,
          WorkName: row.WorkName,
          Room: row.Room,
          Inventory: row.Inventory,
          Device: row.Device,
          PointName: row.PointName,
          ScheduledTime: row.ScheduledTime ? dateFormat.viewDateTimeFormatTZ(row.ScheduledTime) : null,
          WorkOrderId: row.WorkOrderId,
          AnswerValue: row.AnswerValue,
          ExpectableValue: row.ExpectableValue
        });
      });

      // Baslik satiri vurgulanir; beklenen degerden sapan (ExpectableValue = "No") satirlar kirmizi.
      sheet.eachRow((sheetRow, sheetRowNumber) => {
        if (sheetRowNumber == 1) {
          sheet.autoFilter = `${sheet.getColumn(1).letter + "1:" + sheet.getColumn(sheet.columns.length).letter + "1"}`;
          sheetRow.font = { bold: true, size: 12 };
          sheetRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0042a5f5' } };
        }
        else if (sheetRow.getCell(10).value === "No") {
          sheetRow.font = { color: { argb: '00ff0000'}, bold: true, size: 10 };
        }
        else {
          sheetRow.font = { bold: false, size: 10 };
        }

        sheetRow.commit();
      });

      await this.generateReports("PointAnalysisReport", undefined, startDate, endDate, workbook);
    },
  },
};
</script>

<style scoped>
.report-list {
  padding: 1rem 0;
  border-right: 1px solid #ebedf2;
}

.report-list__group + .report-list__group {
  margin-top: 0.75rem;
}

/* text-transform: uppercase kullanilmadi: Turkce "i" harfini noktasiz "I" yapiyor. */
.report-list__group-title {
  padding: 0.5rem 1.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #9699a2;
}

.report-list__item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.7rem 1.5rem;
  text-align: left;
  color: #575962;
  background: transparent;
  border: 0;
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.report-list__item i {
  width: 1.25rem;
  margin-right: 0.75rem;
  text-align: center;
  color: #9699a2;
}

.report-list__item:hover,
.report-list__item:focus {
  background: #f7f8fa;
  outline: none;
}

.report-list__item--active,
.report-list__item--active:hover,
.report-list__item--active:focus {
  font-weight: 600;
  color: #5867dd;
  background: rgba(88, 103, 221, 0.08);
  border-left-color: #5867dd;
}

.report-list__item--active i {
  color: #5867dd;
}

.report-detail {
  padding: 2rem 2.25rem;
}

.report-detail__header {
  margin-bottom: 1.5rem;
}

.report-detail__title {
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #575962;
}

.report-detail__description {
  max-width: 40rem;
  margin: 0;
  color: #7b7e8a;
}

.report-detail__note {
  max-width: 34rem;
  margin-bottom: 1rem;
}

.report-detail__points {
  margin-top: 1.5rem;
}

.report-detail__section-title {
  margin-bottom: 1rem;
  font-weight: 600;
  color: #575962;
}

.report-period {
  display: inline-block;
  max-width: 100%;
  padding: 0.75rem 1rem;
  background: #f7f8fa;
  border-radius: 6px;
}

.report-period__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0.6rem;
}

.report-period__title {
  margin-right: 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #575962;
}

.report-period__preset {
  padding: 0.1rem 0.6rem;
  margin-right: 0.3rem;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #7b7e8a;
  background: #fff;
  border: 1px solid #e2e5ec;
  border-radius: 1rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.report-period__preset:hover,
.report-period__preset:focus {
  color: #5867dd;
  border-color: #5867dd;
  outline: none;
}

.report-period__preset--active,
.report-period__preset--active:hover,
.report-period__preset--active:focus {
  color: #fff;
  background: #5867dd;
  border-color: #5867dd;
}

.report-period__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

/* position: relative, tarih secicinin acilir takvimi input'a gore konumlansin diye. */
.report-period__date {
  position: relative;
  width: 8.5rem;
}

.report-period__separator {
  margin: 0 0.4rem;
  color: #9699a2;
}

/* Tarih kutulari ve indirme dugmesi ayni yukseklikte: ikisinin de dolgusu burada sabitlenir. */
.report-period__date input,
.report-period__download {
  height: 2.5rem;
  padding-top: 0;
  padding-bottom: 0;
}

.report-period__date input {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.report-period__download {
  display: inline-flex;
  align-items: center;
}

.report-period__row .report-period__download {
  margin-left: 0.75rem;
}

.report-period__download i {
  margin-right: 0.5rem;
}

@media (max-width: 991.98px) {
  .report-list {
    border-right: 0;
    border-bottom: 1px solid #ebedf2;
  }

  .report-detail {
    padding: 1.5rem;
  }
}
</style>
